import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, message } from "antd";
import dayjs from "dayjs";
import { UiButton } from "../../ui";
import CgIcon from "../common/CgIcon";
import {
  ListScreen,
  ListHead,
  DistBand,
  ListCard,
  IconAction,
  RowActions,
  RefCell,
  StatusPill,
  DimCell,
  pageSizeOf,
  PHARMACY_PAGE_SIZE,
  ListTable,
} from "../common/ListScreen";
import { getTablePagination } from "../../../utils/tablePagination";
import { useBucketCounts } from "../common/bucketCounts";
import {
  getPharmacyPurchaseEntriesListApi,
  cancelPharmacyPurchaseEntryApi,
} from "../../../services/pharmacyPurchase.services";
import ScanInvoiceModal from "./ScanInvoiceModal";
import { canCancelDocument, cancelFailureMessage } from "../common/cancelWindow";
import { useIsPharmacyAdmin } from "../Users/PharmacyUsers";


/*
 * A purchase entry is a draft until it is either completed - at which point
 * the goods are written into stock - or called off. Those are the three states
 * the API counts it in, and the band is built from the same three.
 */
const BUCKETS = [
  { key: "draft", label: "Draft", color: "#EAAA5E" },
  { key: "completed", label: "Completed", color: "#12B76A" },
  { key: "cancelled", label: "Cancelled", color: "#F97066" },
];

const STATUS_TONE = {
  draft: ["#FFFAEB", "#B54708"],
  completed: ["#ECFDF3", "#027A48"],
  cancelled: ["#FEF3F2", "#B42318"],
};

/* A status the server has that this screen does not know about reads as a
   state rather than as an alarm - cancelled is the red one, and a pill that
   borrowed it would say the wrong thing. */
const NEUTRAL_TONE = ["#F2F4F7", "#475467"];

const prettyStatus = (value) =>
  String(value || "")
    .split("_")
    .join(" ")
    .replace(/^./, (c) => c.toUpperCase());

const PurchaseEntryList = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const isPharmacyAdmin = useIsPharmacyAdmin();

  /* Calling an entry off changes what the list and the band say about it, so
     the page is read again rather than patched in the browser. */
  const refreshPage = () => setRefresh((prev) => !prev);

  useEffect(() => {
    setLoading(true);
    const queryParams = { page, limit: PHARMACY_PAGE_SIZE };
    if (status) queryParams.status = status;
    if (search) queryParams.search = search;
    getPharmacyPurchaseEntriesListApi(new URLSearchParams(queryParams))
      .then((res) => setData(res?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [page, search, status, refresh]);

  const counts = useBucketCounts({
    buckets: BUCKETS,
    response: data,
    activeKey: status,
    search,
  });

  /*
   * The vendors on the page already in hand.
   *
   * It used to come from a second, unlimited call for the whole list. It is a
   * count over every entry, so it is only honest when the page actually is
   * every entry - a page of 25 out of 99 would name a quarter of the vendors
   * as all of them. A longer list says nothing rather than something wrong.
   */
  const band = useMemo(() => {
    const rows = data?.results || [];
    const count = Number(data?.count) || 0;
    const whole = rows.length >= count && !status && !search;

    return {
      vendors: whole ? new Set(rows.map((r) => r.vendor).filter(Boolean)).size : 0,
    };
  }, [data, status, search]);

  /*
   * Calling an entry off from the row.
   *
   * The same question the foot of the entry itself asks, asked here so a draft
   * raised by mistake does not have to be opened to be called off. Who may
   * cancel what - and until when - is the module's one rule, so the row offers
   * the control exactly where the entry screen would offer it.
   */
  const askToCancel = (record) => {
    Modal.confirm({
      title: "Cancel this purchase entry?",
      content: "Are you sure? This cannot be undone.",
      okText: "Yes, cancel it",
      okButtonProps: { danger: true },
      cancelText: "No",
      onOk: () => {
        setCancelling(record.id);
        return cancelPharmacyPurchaseEntryApi(record.id)
          .then(() => {
            message.success("Purchase entry cancelled");
            refreshPage();
          })
          .catch((err) =>
            message.error(
              cancelFailureMessage(err, "Failed to cancel the purchase entry")
            )
          )
          .finally(() => setCancelling(null));
      },
    });
  };

  const columns = [
    {
      title: "Entry Number",
      dataIndex: "entry_number",
      key: "entry_number",
      render: (value) => <RefCell>{value}</RefCell>,
    },
    { title: "Vendor", dataIndex: "vendor_name", key: "vendor_name" },
    {
      title: "Purchase Date",
      dataIndex: "purchase_date",
      key: "purchase_date",
      render: (value) => (
        <DimCell>{value ? dayjs(value).format("DD MMM YYYY") : "-"}</DimCell>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <StatusPill tone={STATUS_TONE[value] || NEUTRAL_TONE}>
          {prettyStatus(value)}
        </StatusPill>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      /*
       * The actions stay against the right edge while the rest of the table
       * scrolls under them. A wide list pushed this column off the end, where
       * its heading read as a cut-off word and its marks could not be reached
       * without scrolling to find them.
       */
      fixed: "right",
      width: 128,
      render: (_, record) => (
        <RowActions>
          <IconAction
            icon="forms"
            label="View purchase entry"
            onClick={() => navigate(`/pharmacy/purchase-entry/${record.id}`)}
          />
          {/* An entry nobody can call off any more gets no cross - a control
              that only ever refuses is worse than one that is not there. */}
          {canCancelDocument(record, { isAdmin: isPharmacyAdmin }) ? (
            <IconAction
              icon="close"
              tone="danger"
              label="Cancel purchase entry"
              disabled={cancelling === record.id}
              onClick={() => askToCancel(record)}
            />
          ) : null}
        </RowActions>
      ),
    },
  ];

  const total = Number(data?.count) || 0;

  return (
    <ListScreen>
      <ListHead
        title="Purchase Entry"
        actions={
          <>
            <UiButton variant="secondary" onClick={() => setScanOpen(true)}>
              <CgIcon name="upload" size={16} stroke={2} />
              Upload Invoice
            </UiButton>
            <UiButton onClick={() => navigate("/pharmacy/purchase-entry/new")}>
              <CgIcon name="plus" size={16} stroke={2.2} />
              Add Purchase Entry
            </UiButton>
          </>
        }
      />

      <DistBand
        total={counts.ALL ?? BUCKETS.reduce((sum, b) => sum + (counts[b.key] || 0), 0)}
        label="Purchase Entries"
        segments={BUCKETS.map((b) => ({ ...b, count: counts[b.key] ?? 0 }))}
        activeKey={status}
        onSelect={(key) => {
          setPage(1);
          // Clicking the state already being shown clears the filter.
          setStatus((prev) => (prev === key ? null : key));
        }}
        meta={band.vendors ? `${band.vendors} vendors on file` : undefined}
      />

      <ListCard
        title="Purchase Entries"
        range={{ page, pageSize: pageSizeOf(data, PHARMACY_PAGE_SIZE), total: total }}
        count={total}
        search={{
          value: search,
          onChange: (e) => {
            setSearch(e?.target?.value || "");
            setPage(1);
          },
          placeholder: "Search entry number or vendor",
        }}
        onClear={
          search || status
            ? () => {
                setSearch("");
                setStatus(null);
                setPage(1);
              }
            : undefined
        }
      >
        <ListTable
          loading={loading}
          dataSource={data?.results || []}
          columns={columns}
          rowKey="id"
          pagination={getTablePagination({
            current: page,
            pageSize: pageSizeOf(data, PHARMACY_PAGE_SIZE),
            total,
            onChange: (p) => setPage(p),
          })}
        />
      </ListCard>

      {scanOpen && (
        <ScanInvoiceModal
          open={scanOpen}
          setOpen={setScanOpen}
          onUse={(scan) => {
            navigate("/pharmacy/purchase-entry/new", { state: { scan } });
          }}
        />
      )}
    </ListScreen>
  );
};

export default PurchaseEntryList;
